import React from "react";
import html2canvas from "html2canvas";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useDataProvider } from "react-admin";
import { getBillStatusColor, getBillStatusTitle } from "../../constants/BillStatus";
import { getBillTypeColor, getBillTypeTitle } from "../../constants/BillType";
import { formatDate } from "../../utils/common";

type BillingRecord = Record<string, unknown> & {
  id?: string | number;
  _id?: string | number;
};

type UserRecord = Record<string, unknown> & {
  id?: string;
  _id?: string;
};

const getFirstDefined = (values: Array<unknown>): unknown => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return null;
};

const parseStringValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
};

const parseNumberValue = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const formatFenToYuan = (value: unknown): string => {
  const fen = parseNumberValue(value);
  if (fen === null) return "-";
  const yuan = fen / 100;
  const normalized = Math.abs(yuan) < 1e-9 ? 0 : yuan;
  return `${normalized.toFixed(2)} 元`;
};

const formatDateValue = (value: unknown): string => {
  const normalized = parseStringValue(value);
  return normalized ? formatDate(normalized) : "";
};

const parseBillingRecord = (raw: unknown): BillingRecord | null => {
  if (raw === null || raw === undefined) return null;
  if (Array.isArray(raw)) return parseBillingRecord(raw[0]);
  if (typeof raw === "object") {
    const record = raw as Record<string, unknown>;
    if (record.data !== undefined) {
      return parseBillingRecord(record.data);
    }
    return record as BillingRecord;
  }
  return null;
};

const parseUserRecord = (raw: unknown): UserRecord | null => {
  if (raw === null || raw === undefined) return null;
  if (Array.isArray(raw)) return parseUserRecord(raw[0]);
  if (typeof raw === "object") {
    const record = raw as Record<string, unknown>;
    if (record.data !== undefined) {
      return parseUserRecord(record.data);
    }
    return record as UserRecord;
  }
  return null;
};

const getRecordValue = (record: BillingRecord | null, keys: string[]): unknown => {
  if (!record) return null;
  return getFirstDefined(keys.map((key) => record[key]));
};

const normalizeUserId = (value: unknown): string => parseStringValue(value);

const getUserIdFromRecord = (record: UserRecord): string => {
  return parseStringValue(record.id ?? record._id);
};

const getUserDisplayName = (record: UserRecord | null): string => {
  if (!record) return "";
  return parseStringValue(record.name ?? record.username ?? record.realname ?? record.nickname);
};

const getUserDisplayPhone = (record: UserRecord | null): string => {
  if (!record) return "";
  return parseStringValue(record.phone ?? record.mobile ?? record.tel);
};

const getUserDisplayAvatar = (record: UserRecord | null): string => {
  if (!record) return "";
  return parseStringValue(record.avatar ?? record.photo ?? record.headimgurl);
};

const renderText = (value: string): React.ReactNode => {
  if (!value) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }
  return <Typography variant="body2">{value}</Typography>;
};

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ mt: 0.5, minHeight: 24, display: "flex", alignItems: "center", wordBreak: "break-all" }}>
        {value}
      </Box>
    </Box>
  );
};

const UserSummaryCard: React.FC<{
  label: string;
  userId: string;
  user: UserRecord | null;
}> = ({ label, userId, user }) => {
  const name = getUserDisplayName(user);
  const phone = getUserDisplayPhone(user);
  const avatar = getUserDisplayAvatar(user);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        px: 2,
        py: 1.5,
        backgroundColor: "background.paper",
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}>
        <Avatar src={avatar || undefined} alt={name || label} sx={{ width: 36, height: 36 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
            {name || "-"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", lineHeight: 1.4, wordBreak: "break-all" }}
          >
            {userId || "-"}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
            {phone || "-"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export const OnlineBillDetailDialog: React.FC<{
  open: boolean;
  billId: string;
  onClose: () => void;
}> = ({ open, billId, onClose }) => {
  const dataProvider = useDataProvider();
  const captureRef = React.useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [captureError, setCaptureError] = React.useState("");
  const [capturing, setCapturing] = React.useState(false);
  const [record, setRecord] = React.useState<BillingRecord | null>(null);
  const [usersById, setUsersById] = React.useState<Record<string, UserRecord>>({});

  const normalizedBillId = React.useMemo(() => parseStringValue(billId), [billId]);

  React.useEffect(() => {
    if (!open || !normalizedBillId) return;

    let cancelled = false;

    const fetchDetail = async () => {
      try {
        setLoading(true);
        setError("");
        setCaptureError("");
        setRecord(null);
        setUsersById({});

        const response = await dataProvider.getOne("billings", { id: normalizedBillId });
        if (cancelled) return;

        const nextRecord = parseBillingRecord(response.data);
        if (!nextRecord) {
          setError("未获取到账单详情");
          return;
        }

        setRecord(nextRecord);

        const payerId = normalizeUserId(
          getFirstDefined([nextRecord.payer, nextRecord.payer_id, nextRecord.payerId]),
        );
        const payeeId = normalizeUserId(
          getFirstDefined([nextRecord.payee, nextRecord.payee_id, nextRecord.payeeId]),
        );
        const userIds = Array.from(new Set([payerId, payeeId].filter((id): id is string => Boolean(id))));

        if (userIds.length === 0) return;

        try {
          const usersResponse = await dataProvider.getMany("users", { ids: userIds });
          if (cancelled) return;

          const nextUsers: Record<string, UserRecord> = {};
          const userRecords = Array.isArray(usersResponse.data) ? usersResponse.data : [];
          for (const rawUser of userRecords) {
            const userRecord = parseUserRecord(rawUser);
            if (!userRecord) continue;
            const userRecordId = getUserIdFromRecord(userRecord);
            if (!userRecordId) continue;
            nextUsers[userRecordId] = userRecord;
          }
          setUsersById(nextUsers);
        } catch {
          if (cancelled) return;
        }
      } catch {
        if (cancelled) return;
        setError("获取账单详情失败");
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    };

    void fetchDetail();

    return () => {
      cancelled = true;
    };
  }, [dataProvider, open, normalizedBillId]);

  const currentBillId = React.useMemo(() => {
    return parseStringValue(getFirstDefined([record?.id, record?._id, normalizedBillId]));
  }, [record, normalizedBillId]);

  const title = parseStringValue(getRecordValue(record, ["title", "name", "subject"]));
  const channel = parseStringValue(getRecordValue(record, ["channel", "pay_channel", "payment_channel"]));
  const amount = getRecordValue(record, ["amount", "total_amount", "money"]);
  const typeCode = parseNumberValue(getRecordValue(record, ["type", "bill_type"]));
  const statusCode = parseNumberValue(getRecordValue(record, ["status", "bill_status"]));
  const payerId = normalizeUserId(getRecordValue(record, ["payer", "payer_id", "payerId"]));
  const payeeId = normalizeUserId(getRecordValue(record, ["payee", "payee_id", "payeeId"]));
  const tradeNo = parseStringValue(
    getRecordValue(record, ["trade_no", "alipay_trade_no", "out_trade_no", "transaction_id"]),
  );
  const bizNo = parseStringValue(
    getRecordValue(record, ["biz_no", "order_no", "order_id", "target_id", "ref_id"]),
  );
  const createdAt = getRecordValue(record, ["created_at", "createdAt", "created"]);
  const updatedAt = getRecordValue(record, ["updated_at", "updatedAt", "updated"]);
  const paidAt = getRecordValue(record, ["paid_at", "paidAt", "pay_time", "payment_time"]);
  const landedAt = getRecordValue(record, ["landed_at", "landedAt", "arrived_at", "arrivedAt"]);
  const remark = parseStringValue(getRecordValue(record, ["remark", "description", "desc", "note"]));

  const jsonText = React.useMemo(() => {
    if (!record) return "";
    try {
      return JSON.stringify(record, null, 2);
    } catch {
      return String(record);
    }
  }, [record]);

  const captureFileName = React.useMemo(() => {
    const raw = currentBillId || normalizedBillId || "detail";
    const safe = raw.replace(/[^a-zA-Z0-9_-]+/g, "-");
    return `bill-${safe}.png`;
  }, [currentBillId, normalizedBillId]);

  const handleCopyBillId = async () => {
    if (!currentBillId) return;
    try {
      await navigator.clipboard.writeText(currentBillId);
    } catch {
      void 0;
    }
  };

  const handleCapture = async () => {
    if (!captureRef.current || capturing || !record) return;

    try {
      setCaptureError("");
      setCapturing(true);
      const canvas = await html2canvas(captureRef.current, {
        backgroundColor: "#ffffff",
        scale: Math.min(window.devicePixelRatio || 1, 2),
        useCORS: true,
        logging: false,
      });
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = captureFileName;
      link.click();
    } catch {
      setCaptureError("截图失败，请稍后重试");
    } finally {
      setCapturing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Box ref={captureRef}>
        <DialogTitle>账单详情</DialogTitle>
        <DialogContent dividers>
          {loading && <Typography variant="body2">正在加载账单详情...</Typography>}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {!loading && !error && record && (
            <Stack spacing={2}>
              <Box
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.default",
                  px: 2,
                  py: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ wordBreak: "break-all" }}>
                      {title || "未命名账单"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                      账单ID：{currentBillId || "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    {typeCode !== null && (
                      <Chip
                        label={getBillTypeTitle(typeCode)}
                        size="small"
                        sx={{ color: "#fff", backgroundColor: getBillTypeColor(typeCode) }}
                      />
                    )}
                    {statusCode !== null && (
                      <Chip
                        label={getBillStatusTitle(statusCode)}
                        size="small"
                        sx={{ color: "#fff", backgroundColor: getBillStatusColor(statusCode) }}
                      />
                    )}
                    <Typography variant="h6" sx={{ whiteSpace: "nowrap" }}>
                      {formatFenToYuan(amount)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  gap: 2,
                }}
              >
                <DetailField label="付款对象" value={renderText(title)} />
                <DetailField label="渠道" value={renderText(channel)} />
                <DetailField label="创建时间" value={renderText(formatDateValue(createdAt))} />
                <DetailField label="更新时间" value={renderText(formatDateValue(updatedAt))} />
                <DetailField label="支付时间" value={renderText(formatDateValue(paidAt))} />
                <DetailField label="到账时间" value={renderText(formatDateValue(landedAt))} />
                <DetailField label="交易单号" value={renderText(tradeNo)} />
                <DetailField label="业务单号" value={renderText(bizNo)} />
              </Box>

              <Divider />

              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                <UserSummaryCard label="付款方" userId={payerId} user={usersById[payerId] ?? null} />
                <UserSummaryCard label="收款方" userId={payeeId} user={usersById[payeeId] ?? null} />
              </Box>

              {remark && <DetailField label="备注" value={renderText(remark)} />}
            </Stack>
          )}
        </DialogContent>
      </Box>
      {captureError && (
        <Box sx={{ px: 3, pt: 2 }}>
          <Alert severity="error">{captureError}</Alert>
        </Box>
      )}
      <DialogActions>
        <Button onClick={handleCapture} disabled={loading || capturing || !record}>
          {capturing ? "截图中..." : "截图"}
        </Button>
        <Button onClick={handleCopyBillId} disabled={!currentBillId}>
          复制账单ID
        </Button>
        <Button onClick={onClose} variant="contained">
          关闭
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OnlineBillDetailDialog;
